import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, formatApiErrorDetail } from '../utils/api';
import { Plus, Edit, Trash2, Save, X, Link as LinkIcon, FileText, Wand2, Upload, Image as ImageIcon, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import BACKEND_BASE from '../utils/backend';
import useProductCategories from '../utils/useProductCategories';
import ProductCategorySelect from '../components/ProductCategorySelect';

const API_BASE = BACKEND_BASE || '';

const EMPTY_FORM = {
  name: '',
  dosage: '',
  temperature: '',
  usage: '',
  standards: '',
  default_method_id: null,
  product_type: null,
  product_photo: null,
  technical_sheet_file: null,
  safety_sheet_file: null,
  technical_sheet_url: '',
  safety_sheet_url: '',
};

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [methods, setMethods] = useState([]);
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [uploading, setUploading] = useState('');
  const [classifying, setClassifying] = useState(false);
  const { categories: PRODUCT_TYPES, create: createCategory } = useProductCategories();

  useEffect(() => {
    Promise.all([loadProducts(), loadMethods(), loadIcons()]).finally(() => setLoading(false));
  }, []);

  const handleAutoClassify = async () => {
    setClassifying(true);
    try {
      const { data } = await api.post('/products/auto-classify');
      if (data.classified > 0) {
        toast.success(`${data.classified} produit(s) classifie(s) automatiquement`);
        loadProducts();
      } else {
        toast.info('Tous les produits sont deja classifies');
      }
    } catch (error) {
      toast.error('Erreur lors de la classification');
    } finally {
      setClassifying(false);
    }
  };

  const loadProducts = async () => {
    try {
      const { data } = await api.get('/products');
      setProducts(data);
    } catch (error) {
      toast.error('Echec du chargement des produits');
    }
  };

  const loadMethods = async () => {
    try {
      const { data } = await api.get('/methods');
      setMethods(data);
    } catch (error) {
      toast.error('Echec du chargement des methodes');
    }
  };

  const loadIcons = async () => {
    try {
      const { data } = await api.get('/icons');
      setIcons(data);
    } catch (error) {
      // Silently fail
    }
  };

  const uploadFile = async (file, endpoint, field) => {
    if (!file) return;
    setUploading(field);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({ ...prev, [field]: data.url }));
      toast.success('Fichier uploade');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Echec de l\'upload');
    } finally {
      setUploading('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        default_method_id: formData.default_method_id || null,
        product_type: formData.product_type || null,
        technical_sheet_url: (formData.technical_sheet_url || '').trim() || null,
        safety_sheet_url: (formData.safety_sheet_url || '').trim() || null,
      };
      if (editingId) {
        await api.put(`/products/${editingId}`, payload);
        toast.success('Produit modifie');
      } else {
        await api.post('/products', payload);
        toast.success('Produit cree');
      }
      resetForm();
      loadProducts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    }
  };

  const handleEdit = (product) => {
    // Use the unified ProductCreate page so create and edit have the same form
    navigate(`/products/new?id=${product.id}`);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce produit ?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Produit supprime');
      loadProducts();
    } catch (error) {
      toast.error('Echec de la suppression');
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditingId(null);
    setShowForm(false);
  };

  const getMethodById = (id) => methods.find((m) => m.id === id);
  const getIconById = (id) => icons.find((i) => i.id === id);

  const renderMethodSteps = (method) => {
    if (!method || !method.steps || method.steps.length === 0) return null;
    const sorted = [...method.steps].sort((a, b) => (a.order || 0) - (b.order || 0));
    return (
      <div className="flex items-center gap-1 mt-1">
        {sorted.map((step, i) => {
          const icon = step.icon_id ? getIconById(step.icon_id) : null;
          return (
            <div key={i} className="flex items-center gap-0.5">
              {i > 0 && <span className="text-xs" style={{ color: '#9CA3AF' }}>&rarr;</span>}
              <div className="flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ backgroundColor: '#F3F4F6' }}>
                {icon?.image_url && (
                  <img src={API_BASE + icon.image_url} alt={icon.name} className="w-5 h-5 object-contain" />
                )}
                <span className="text-xs" style={{ color: '#374151' }}>{step.step_name}</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12">Chargement...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="products-page">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#0E2D43', fontFamily: 'Work Sans, sans-serif' }}>
            Produits
          </h1>
          {!showForm && (
            <div className="flex gap-3">
              <button onClick={handleAutoClassify} disabled={classifying}
                data-testid="auto-classify-btn"
                className="flex items-center space-x-2 px-4 py-2 rounded font-medium border disabled:opacity-50"
                style={{ borderColor: '#7C3AED', color: '#7C3AED' }}>
                <Wand2 size={18} className={classifying ? 'animate-spin' : ''} />
                <span>{classifying ? 'Classification...' : 'Auto-classifier'}</span>
              </button>
              <Link to="/products/new" data-testid="new-product-button"
                className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
                style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                <FileText size={20} />
                <span>Nouveau Produit (avec fiche technique)</span>
              </Link>
              <button onClick={() => setShowForm(true)} data-testid="add-product-button"
                className="flex items-center space-x-2 px-4 py-2 rounded font-medium border"
                style={{ borderColor: '#0E2D43', color: '#0E2D43' }}>
                <Plus size={20} />
                <span>Ajout rapide</span>
              </button>
            </div>
          )}
        </div>

        {showForm && (
          <div className="bg-white rounded-lg shadow p-6 border mb-8" style={{ borderColor: '#E5E7EB' }}>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#0E2D43' }}>
              {editingId ? 'Modifier Produit' : 'Nouveau Produit'}
            </h2>
            <form onSubmit={handleSubmit} data-testid="product-form">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Nom *</label>
                  <input type="text" value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="product-name-input"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: Prest'hyg Food Alcalinobact" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Dosage</label>
                  <input type="text" value={formData.dosage}
                    onChange={(e) => setFormData({ ...formData, dosage: e.target.value })}
                    data-testid="product-dosage-input"
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: 5% ou 50ml/L" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Temperature</label>
                  <input type="text" value={formData.temperature}
                    onChange={(e) => setFormData({ ...formData, temperature: e.target.value })}
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: Eau froide, Eau tiede" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Type de produit</label>
                  <ProductCategorySelect
                    value={formData.product_type}
                    onChange={(val) => setFormData({ ...formData, product_type: val })}
                    categories={PRODUCT_TYPES}
                    onCreate={createCategory}
                    dataTestId="product-type-select"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>Usage</label>
                  <input type="text" value={formData.usage}
                    onChange={(e) => setFormData({ ...formData, usage: e.target.value })}
                    className="w-full px-4 py-2 border rounded" style={{ borderColor: '#D1D5DB' }}
                    placeholder="ex: Degraissant surfaces" />
                </div>
              </div>

              {/* Photo produit */}
              <div className="mb-5 p-4 rounded border" style={{ borderColor: '#E5E7EB', backgroundColor: '#FAFAFA' }}>
                <label className="flex items-center gap-2 text-sm font-medium mb-3" style={{ color: '#0E2D43' }}>
                  <ImageIcon size={16} /> Photo produit (JPG, PNG, WEBP)
                </label>
                <div className="flex items-center gap-4">
                  <div className="relative border-2 border-dashed rounded-lg text-center transition-colors cursor-pointer flex items-center justify-center"
                    style={{
                      borderColor: formData.product_photo ? '#6FCF97' : '#D1D5DB',
                      backgroundColor: formData.product_photo ? '#F0FFF4' : '#FFFFFF',
                      width: '140px', height: '100px',
                    }}>
                    {formData.product_photo ? (
                      <img src={API_BASE + formData.product_photo} alt="Photo produit"
                        className="max-w-full max-h-full object-contain rounded" data-testid="product-photo-preview" />
                    ) : (
                      <div className="flex flex-col items-center gap-1">
                        <Upload size={22} style={{ color: '#9CA3AF' }} />
                        <span className="text-[11px]" style={{ color: '#6B7280' }}>Ajouter</span>
                      </div>
                    )}
                    <input type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                      data-testid="product-photo-input"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) => uploadFile(e.target.files[0], '/products/upload-photo', 'product_photo')} />
                  </div>
                  <div className="flex flex-col gap-2">
                    {uploading === 'product_photo' && (
                      <span className="text-xs" style={{ color: '#4A90D9' }}>Upload en cours...</span>
                    )}
                    {formData.product_photo && (
                      <>
                        <label className="text-xs px-3 py-1 rounded border cursor-pointer inline-flex items-center gap-1"
                          style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}>
                          <Upload size={12} /> Remplacer
                          <input type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                            className="hidden"
                            onChange={(e) => uploadFile(e.target.files[0], '/products/upload-photo', 'product_photo')} />
                        </label>
                        <button type="button"
                          data-testid="remove-product-photo"
                          onClick={() => setFormData(prev => ({ ...prev, product_photo: null }))}
                          className="text-xs px-3 py-1 rounded inline-flex items-center gap-1"
                          style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                          <Trash2 size={12} /> Supprimer
                        </button>
                      </>
                    )}
                    <p className="text-[11px]" style={{ color: '#9CA3AF' }}>
                      La photo sera affichee dans la liste et dans le PDF HACCP.
                    </p>
                  </div>
                </div>
              </div>

              {/* FT / FDS: file OR URL */}
              <div className="grid grid-cols-2 gap-4 mb-5">
                {/* Fiche Technique */}
                <div className="p-4 rounded border" style={{ borderColor: '#E5E7EB', backgroundColor: '#FAFAFA' }}>
                  <label className="flex items-center gap-2 text-sm font-medium mb-3" style={{ color: '#0E2D43' }}>
                    <FileText size={16} /> Fiche Technique (FT)
                  </label>
                  <div className="relative border-2 border-dashed rounded p-2 mb-2 text-center cursor-pointer"
                    style={{ borderColor: formData.technical_sheet_file ? '#6FCF97' : '#D1D5DB', backgroundColor: formData.technical_sheet_file ? '#F0FFF4' : '#FFFFFF' }}>
                    {formData.technical_sheet_file ? (
                      <span className="text-xs font-medium" style={{ color: '#065F46' }}>PDF uploade</span>
                    ) : (
                      <span className="text-xs" style={{ color: '#6B7280' }}>Uploader un PDF</span>
                    )}
                    <input type="file" accept=".pdf,application/pdf"
                      data-testid="ft-file-input"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) => uploadFile(e.target.files[0], '/products/upload-sheet', 'technical_sheet_file')} />
                  </div>
                  {formData.technical_sheet_file && (
                    <button type="button" onClick={() => setFormData(p => ({ ...p, technical_sheet_file: null }))}
                      className="text-[11px] mb-2 px-2 py-0.5 rounded" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                      <Trash2 size={10} className="inline mr-1" /> Retirer le fichier
                    </button>
                  )}
                  <label className="block text-[11px] font-medium mb-1" style={{ color: '#6B7280' }}>Ou coller un lien web</label>
                  <input type="url" placeholder="https://..."
                    data-testid="ft-url-input"
                    value={formData.technical_sheet_url || ''}
                    onChange={(e) => setFormData({ ...formData, technical_sheet_url: e.target.value })}
                    className="w-full px-2 py-1.5 border rounded text-xs" style={{ borderColor: '#D1D5DB' }} />
                </div>

                {/* Fiche Donnees de Securite */}
                <div className="p-4 rounded border" style={{ borderColor: '#E5E7EB', backgroundColor: '#FAFAFA' }}>
                  <label className="flex items-center gap-2 text-sm font-medium mb-3" style={{ color: '#0E2D43' }}>
                    <FileText size={16} /> Fiche Donnees de Securite (FDS)
                  </label>
                  <div className="relative border-2 border-dashed rounded p-2 mb-2 text-center cursor-pointer"
                    style={{ borderColor: formData.safety_sheet_file ? '#6FCF97' : '#D1D5DB', backgroundColor: formData.safety_sheet_file ? '#F0FFF4' : '#FFFFFF' }}>
                    {formData.safety_sheet_file ? (
                      <span className="text-xs font-medium" style={{ color: '#065F46' }}>PDF uploade</span>
                    ) : (
                      <span className="text-xs" style={{ color: '#6B7280' }}>Uploader un PDF</span>
                    )}
                    <input type="file" accept=".pdf,application/pdf"
                      data-testid="fds-file-input"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={(e) => uploadFile(e.target.files[0], '/products/upload-sheet', 'safety_sheet_file')} />
                  </div>
                  {formData.safety_sheet_file && (
                    <button type="button" onClick={() => setFormData(p => ({ ...p, safety_sheet_file: null }))}
                      className="text-[11px] mb-2 px-2 py-0.5 rounded" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                      <Trash2 size={10} className="inline mr-1" /> Retirer le fichier
                    </button>
                  )}
                  <label className="block text-[11px] font-medium mb-1" style={{ color: '#6B7280' }}>Ou coller un lien web</label>
                  <input type="url" placeholder="https://..."
                    data-testid="fds-url-input"
                    value={formData.safety_sheet_url || ''}
                    onChange={(e) => setFormData({ ...formData, safety_sheet_url: e.target.value })}
                    className="w-full px-2 py-1.5 border rounded text-xs" style={{ borderColor: '#D1D5DB' }} />
                </div>
              </div>

              {/* Default Method Selection */}
              <div className="mb-4 p-4 rounded border" style={{ borderColor: '#DBEAFE', backgroundColor: '#EFF6FF' }}>
                <label className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: '#0E2D43' }}>
                  <LinkIcon size={16} />
                  Methode de nettoyage par defaut
                </label>
                <p className="text-xs mb-3" style={{ color: '#6B7280' }}>
                  Quand ce produit est selectionne dans un plan, la methode sera automatiquement remplie.
                </p>
                <select
                  value={formData.default_method_id || ''}
                  onChange={(e) => setFormData({ ...formData, default_method_id: e.target.value || null })}
                  data-testid="product-method-select"
                  className="w-full px-4 py-2 border rounded"
                  style={{ borderColor: '#D1D5DB' }}
                >
                  <option value="">-- Aucune methode par defaut --</option>
                  {methods.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
                {formData.default_method_id && renderMethodSteps(getMethodById(formData.default_method_id))}
              </div>

              <div className="flex space-x-3">
                <button type="submit" data-testid="save-product-button"
                  className="flex items-center space-x-2 px-4 py-2 rounded font-medium"
                  style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
                  <Save size={18} />
                  <span>Enregistrer</span>
                </button>
                <button type="button" onClick={resetForm}
                  className="flex items-center space-x-2 px-4 py-2 rounded border"
                  style={{ borderColor: '#D1D5DB', color: '#0E2D43' }}>
                  <X size={18} />
                  <span>Annuler</span>
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-white rounded-lg shadow overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
          <table className="w-full">
            <thead style={{ backgroundColor: '#0E2D43', color: '#FFFFFF' }}>
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">Photo</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Nom</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Dosage</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Temperature</th>
                <th className="px-4 py-3 text-left text-sm font-medium">FT/FDS</th>
                <th className="px-4 py-3 text-left text-sm font-medium">Methode par defaut</th>
                <th className="px-4 py-3 text-right text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center" style={{ color: '#6B7280' }}>
                    Aucun produit. Ajoutez votre premier produit !
                  </td>
                </tr>
              ) : (
                products.map((product) => {
                  const method = product.default_method_id ? getMethodById(product.default_method_id) : null;
                  const ftLink = product.technical_sheet_file ? (API_BASE + product.technical_sheet_file) : product.technical_sheet_url;
                  const fdsLink = product.safety_sheet_file ? (API_BASE + product.safety_sheet_file) : product.safety_sheet_url;
                  return (
                    <tr key={product.id} data-testid={`product-row-${product.id}`} className="border-t align-middle" style={{ borderColor: '#E5E7EB' }}>
                      <td className="px-4 py-3">
                        {product.product_photo ? (
                          <img src={API_BASE + product.product_photo}
                            alt={product.name}
                            data-testid={`product-thumb-${product.id}`}
                            className="w-12 h-12 object-contain rounded border"
                            style={{ borderColor: '#E5E7EB' }} />
                        ) : (
                          <div className="w-12 h-12 rounded border flex items-center justify-center"
                            style={{ borderColor: '#E5E7EB', backgroundColor: '#F9FAFB' }}>
                            <span style={{ color: '#D1D5DB', fontSize: '10px' }}>N/A</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium" style={{ color: '#0E2D43' }}>{product.name}</td>
                      <td className="px-4 py-3">
                        {product.product_type ? (
                          (() => {
                            const pt = PRODUCT_TYPES.find(t => t.value === product.product_type);
                            return pt ? (
                              <span className="text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: pt.bg, color: pt.color }}>
                                {pt.label}
                              </span>
                            ) : (
                              <span className="text-xs" style={{ color: '#94A3B8' }}>{product.product_type}</span>
                            );
                          })()
                        ) : (
                          <span className="text-xs" style={{ color: '#CBD5E1' }}>-</span>
                        )}
                      </td>
                      <td className="px-4 py-3" style={{ color: '#6B7280' }}>{product.dosage}</td>
                      <td className="px-4 py-3" style={{ color: '#6B7280' }}>{product.temperature}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {ftLink ? (
                            <a href={ftLink} target="_blank" rel="noopener noreferrer"
                              className="text-[11px] inline-flex items-center gap-1 font-medium" style={{ color: '#1D4ED8' }}>
                              <ExternalLink size={10} /> FT
                            </a>
                          ) : (
                            <span className="text-[11px]" style={{ color: '#CBD5E1' }}>FT -</span>
                          )}
                          {fdsLink ? (
                            <a href={fdsLink} target="_blank" rel="noopener noreferrer"
                              className="text-[11px] inline-flex items-center gap-1 font-medium" style={{ color: '#DC2626' }}>
                              <ExternalLink size={10} /> FDS
                            </a>
                          ) : (
                            <span className="text-[11px]" style={{ color: '#CBD5E1' }}>FDS -</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {method ? (
                          <div>
                            <span className="text-sm font-medium" style={{ color: '#0E2D43' }}>{method.name}</span>
                            {renderMethodSteps(method)}
                          </div>
                        ) : (
                          <span className="text-xs" style={{ color: '#9CA3AF' }}>Aucune</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button onClick={() => handleEdit(product)} data-testid={`edit-product-${product.id}`}
                          className="inline-flex items-center px-3 py-1 rounded mr-2"
                          style={{ backgroundColor: '#F3F4F6', color: '#0E2D43' }}>
                          <Edit size={16} />
                        </button>
                        <button onClick={() => handleDelete(product.id)} data-testid={`delete-product-${product.id}`}
                          className="inline-flex items-center px-3 py-1 rounded"
                          style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
};

export default Products;
